import { useEffect, useMemo, useRef, useState } from 'react';
import { 
  Plus, Search, List, LayoutGrid, GripVertical, 
  Edit3, Trash2, Eye, EyeOff, Save, X, 
  CheckCircle2, AlertCircle, Share2, Info, Building,
  Camera, Globe, Settings, ChevronRight, RefreshCcw, ExternalLink
} from 'lucide-react';
import '../../styles/admin.css';
import { fetchSuppliersAdmin, createSupplier, updateSupplier, deleteSupplier } from '../../lib/suppliers';
import { getFileUrl, pb } from '../../lib/pb';

export default function AdminSuppliers() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [mode, setMode] = useState('create');
  const [viewMode, setViewMode] = useState('grid');
  const [filterStatus, setFilterStatus] = useState('all');
  const [draggingId, setDraggingId] = useState(null);
  const [success, setSuccess] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchSuppliersAdmin(page, 100);
      let list = res.items || [];
      if (search) {
        const q = search.toLowerCase();
        list = list.filter(s => (s.name || '').toLowerCase().includes(q));
      }
      setItems(list);
      setTotalPages(res.totalPages || 1);
    } catch (e) {
      setError(e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page, search]);

  useEffect(() => {
    let unsub = null;
    (async () => {
      try {
        unsub = await pb.collection('suppliers').subscribe('*', () => load());
      } catch (e) { console.error(e); }
    })();
    return () => { if (unsub) pb.collection('suppliers').unsubscribe('*'); };
  }, []);

  const filteredItems = useMemo(() => {
    return items.filter(s => {
      if (filterStatus === 'published' && !s.published) return false;
      if (filterStatus === 'draft' && s.published) return false;
      return true;
    });
  }, [items, filterStatus]);

  const handleDragStart = (id) => setDraggingId(id);

  const handleDragOver = (targetId, e) => {
    e.preventDefault();
    if (!draggingId || draggingId === targetId) return;
    setItems(prev => {
      const next = [...prev];
      const fromIdx = next.findIndex(s => s.id === draggingId);
      const toIdx = next.findIndex(s => s.id === targetId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  };

  const persistOrder = async () => {
    if (!items.length) return;
    try {
      const updates = items.map((s, index) => {
        const newOrder = (index + 1) * 10;
        if (s.order !== newOrder) return updateSupplier(s.id, { order: newOrder });
        return null;
      }).filter(Boolean);
      if (updates.length) {
        await Promise.all(updates);
        setSuccess('Ordre mis à jour ✨');
        setTimeout(() => setSuccess(''), 2000);
      }
    } catch (e) { setError("Erreur de réorganisation"); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    persistOrder();
    setDraggingId(null);
  };

  const handleTogglePublished = async (s) => {
    const oldStatus = s.published;
    setItems(prev => prev.map(item => item.id === s.id ? { ...item, published: !oldStatus } : item));

    try {
      await updateSupplier(s.id, { published: !oldStatus });
      setSuccess(!oldStatus ? 'Fournisseur publié ⭐' : 'Fournisseur masqué 🌑');
      setTimeout(() => setSuccess(''), 2000);
    } catch (e) {
      setItems(prev => prev.map(item => item.id === s.id ? { ...item, published: oldStatus } : item));
      setError(e.message);
    }
  };

  const openCreate = () => {
    setMode('create');
    setEditing({ name: '', website: '', logo: '', published: true });
  };

  const openEdit = (s) => { setMode('edit'); setEditing(s); };

  const handleDelete = async (s) => {
    if (!window.confirm(`Supprimer le fournisseur "${s.name}" ?`)) return;
    try {
      await deleteSupplier(s.id);
      setSuccess('Partenaire supprimé 🗑️');
      setTimeout(() => setSuccess(''), 2000);
    } catch (e) { setError(e.message); }
  };

  return (
    <div className="admin-main animation-fade-in">
      <header className="admin-header-premium">
        <div className="header-content">
          <div className="header-tag">Partenariats</div>
          <h1>Nos Fournisseurs <span className="count-badge">{items.length}</span></h1>
          <p className="header-subtitle">Optimisez la visibilité de vos partenaires technologiques et constructeurs.</p>
        </div>
        <button className="btn-add-premium" onClick={openCreate}>
          <Plus size={20} />
          <span>Ajouter un partenaire</span>
        </button>
      </header>

      {success && <div className="toast-success"><CheckCircle2 size={18}/> {success}</div>}
      {error && <div className="toast-error"><AlertCircle size={18}/> {error}</div>}

      <div className="admin-controls-bar">
        <div className="search-box-premium">
          <Search size={18} className="search-icon" />
          <input 
            placeholder="Rechercher un partenaire..." 
            value={search} 
            onChange={e => { setPage(1); setSearch(e.target.value); }}
          />
        </div>

        <div className="filters-and-view">
          <div className="capsule-select">
            <button className={filterStatus === 'all' ? 'active' : ''} onClick={() => setFilterStatus('all')}>Tous</button>
            <button className={filterStatus === 'published' ? 'active' : ''} onClick={() => setFilterStatus('published')}>Publiés</button>
            <button className={filterStatus === 'draft' ? 'active' : ''} onClick={() => setFilterStatus('draft')}>Brouillons</button>
          </div>

          <div className="view-switcher-premium">
            <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}><List size={18}/></button>
            <button className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')}><LayoutGrid size={18}/></button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="loading-state">
          <RefreshCcw className="spin" size={32} />
          <p>Chargement des partenaires...</p>
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="empty-state-card">
          <Building size={48} />
          <h3>Aucun partenaire pour le moment</h3>
          <p>Enregistrez vos fournisseurs pour les afficher sur la page d'accueil ou "La Société".</p>
          <button className="btn filled" onClick={openCreate}>Ajouter un partenaire</button>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className={`suppliers-content-container ${viewMode}`}>
          {viewMode === 'list' ? (
            <div className="premium-list">
              {filteredItems.map((s) => (
                <div 
                  key={s.id} 
                  className={`list-item-row ${draggingId === s.id ? 'dragging' : ''}`}
                  draggable
                  onDragStart={() => handleDragStart(s.id)}
                  onDragOver={(e) => handleDragOver(s.id, e)}
                  onDrop={handleDrop}
                >
                  <div className="drag-handle"><GripVertical size={18}/></div>
                  <div className="item-icon-box wide">
                    {s.logo ? (
                      <img src={getFileUrl(s, s.logo)} alt={s.name} />
                    ) : (
                      <Building size={20} className="placeholder" />
                    )}
                  </div>
                  <div className="item-info">
                    <div className="item-title">{s.name}</div>
                    <div className="item-meta">
                      {s.website ? (
                         <a href={s.website} target="_blank" rel="noreferrer" className="website-link">
                            {s.website} <ExternalLink size={12}/>
                         </a>
                      ) : "Aucun site rattaché"}
                    </div>
                  </div>
                  <div className="item-status">
                     <button className={`status-pill ${s.published ? 'online' : 'offline'}`} onClick={() => handleTogglePublished(s)}>
                        {s.published ? <Eye size={14}/> : <EyeOff size={14}/>}
                        <span>{s.published ? 'Publié' : 'Masqué'}</span>
                     </button>
                  </div>
                  <div className="item-actions">
                    <button className="action-btn edit" onClick={() => openEdit(s)} title="Modifier">
                      <Edit3 size={18}/>
                    </button>
                    <button className="action-btn delete" onClick={() => handleDelete(s)} title="Supprimer">
                      <Trash2 size={18}/>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="premium-grid-logos">
              {filteredItems.map((s) => (
                <div 
                  key={s.id} 
                  className="supplier-card-premium animation-slide-up"
                  draggable
                  onDragStart={() => handleDragStart(s.id)}
                  onDragOver={(e) => handleDragOver(s.id, e)}
                  onDrop={handleDrop}
                >
                  <div className="card-media logo">
                    {s.logo ? <img src={getFileUrl(s, s.logo)} alt={s.name} /> : <div className="card-placeholder"><Building size={40}/></div>}
                    <div className={`status-dot ${s.published ? 'active' : ''}`}></div>
                  </div>
                  <div className="card-body centered">
                    <h3 className="card-title text-center">{s.name}</h3>
                    <div className="card-actions-row">
                       <button className={`btn-mini ${s.published ? 'online' : 'offline'}`} onClick={() => handleTogglePublished(s)} title={s.published ? 'Masquer' : 'Publier'}>
                          {s.published ? <Eye size={16}/> : <EyeOff size={16}/>}
                       </button>
                       <div className="spacer"></div>
                       <button className="btn-mini edit" onClick={() => openEdit(s)} title="Modifier"><Edit3 size={16}/></button>
                       {s.website && <a href={s.website} target="_blank" rel="noreferrer" className="btn-mini web" title="Voir le site"><Globe size={16}/></a>}
                       <button className="btn-mini delete" onClick={() => handleDelete(s)} title="Supprimer"><Trash2 size={16}/></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {editing && (
        <SupplierStudio
          mode={mode}
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      <style>{`
        .premium-grid-logos { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 20px; padding: 20px 0; }
        
        .supplier-card-premium { background: #fff; border-radius: 24px; border: 1px solid var(--admin-border); overflow: hidden; position: relative; transition: all 0.3s ease; cursor: grab; }
        .supplier-card-premium:hover { transform: translateY(-5px); box-shadow: 0 15px 30px -10px rgba(0,0,0,0.1); border-color: var(--admin-primary); }
        
        .card-media.logo { height: 120px; position: relative; background: #fff; display: flex; align-items: center; justify-content: center; padding: 25px; }
        .card-media.logo img { width: 100%; height: 100%; object-fit: contain; }
        
        .status-dot { position: absolute; top: 12px; right: 12px; width: 8px; height: 8px; border-radius: 50%; background: #cbd5e1; }
        .status-dot.active { background: #10b981; box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.1); }

        .card-body.centered { padding: 15px 20px 20px 20px; display: flex; flex-direction: column; align-items: center; }
        .text-center { text-align: center; }
        
        .card-actions-row { display: flex; gap: 8px; margin-top: 15px; }
        .btn-mini { width: 34px; height: 34px; border-radius: 10px; border: 1px solid #f1f5f9; background: #f8fafc; color: #64748b; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; }
        .btn-mini:hover { background: #fff; border-color: var(--admin-primary); color: var(--admin-primary); transform: scale(1.1); }
        .btn-mini.delete:hover { border-color: #f43f5e; color: #f43f5e; }
        .btn-mini.web:hover { border-color: #3b82f6; color: #3b82f6; }

        .website-link { font-size: 11px; color: #3b82f6; text-decoration: none; font-weight: 700; display: flex; align-items: center; gap: 5px; }
        .website-link:hover { text-decoration: underline; }

        .item-icon-box.wide { width: 100px; height: 50px; background: #fff; padding: 8px; border: 1px solid #f1f5f9; }
        .item-icon-box.wide img { width: 100%; height: 100%; object-fit: contain; }

        .toast-success { position: fixed; bottom: 30px; right: 30px; background: #0f172a; color: #fff; padding: 15px 25px; border-radius: 16px; display: flex; align-items: center; gap: 12px; font-weight: 800; box-shadow: 0 20px 25px -5px rgba(15, 23, 42, 0.3); z-index: 1000; animation: slideInRight 0.3s ease; }
        
        @media (max-width: 600px) {
          .premium-grid-logos { grid-template-columns: 1fr 1fr; }
        }
      `}</style>
    </div>
  );
}

function SupplierStudio({ mode, initial, onClose, onSaved }) {
  const [activeTab, setActiveTab] = useState('identity');
  const [name, setName] = useState(initial.name || '');
  const [website, setWebsite] = useState(initial.website || '');
  const [published, setPublished] = useState(initial.published ?? true);
  const [logo, setLogo] = useState(() => initial.logo ? { type: 'existing', name: initial.logo } : null);
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const compressImage = (file, maxWidth = 800, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        try {
          let { width, height } = img;
          if (width > maxWidth) { height = (height * maxWidth) / width; width = maxWidth; }
          canvas.width = width; canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Compression error')), 'image/jpeg', quality);
        } catch (err) { reject(err); }
      };
      img.onerror = () => reject(new Error("Image error"));
      img.src = URL.createObjectURL(file);
    });
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      const finalFile = new File([compressed], file.name, { type: 'image/jpeg' });
      setLogo({ type: 'new', file: finalFile });
    } catch (err) { setError("Erreur compression logo"); }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const data = { name, website, published };
    if (logo) { if (logo.type === 'new') data.logo = logo.file; } 
    else { data.logo = null; }

    try {
      if (mode === 'create') await createSupplier(data);
      else await updateSupplier(initial.id, data);
      onSaved();
    } catch (err) { setError(err?.message || 'Erreur sauvegarde'); } 
    finally { setSaving(false); }
  };

  const previewUrl = logo ? (logo.type === 'existing' ? getFileUrl(initial, logo.name) : URL.createObjectURL(logo.file)) : '';

  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal-container compact studio-supplier animation-slide-up">
        <header className="modal-header-premium">
          <div className="header-info">
            <div className="header-tag">{mode === 'create' ? 'Nouveau' : 'Édition'}</div>
            <h2>Studio Partenaire</h2>
          </div>
          <button className="btn-close-modal" onClick={onClose}><X size={20}/></button>
        </header>

        <div className="modal-tabs">
          <button className={activeTab === 'identity' ? 'active' : ''} onClick={() => setActiveTab('identity')}>
            <Building size={16}/> <span>Identité</span>
          </button>
          <button className={activeTab === 'media' ? 'active' : ''} onClick={() => setActiveTab('media')}>
            <Camera size={16}/> <span>Logo</span>
          </button>
        </div>

        <form onSubmit={onSubmit} className="modal-body-premium">
           {error && <div className="modal-error"><AlertCircle size={14}/> {error}</div>}

           <div className="tab-panels">
              {activeTab === 'identity' && (
                <div className="tab-pane active animation-fade-in">
                   <div className="form-group-premium">
                      <label>Dénomination commerciale</label>
                      <input value={name} onChange={e => setName(e.target.value)} required placeholder="Nom du fournisseur..." />
                   </div>
                   <div className="form-group-premium">
                      <label>Lien Website</label>
                      <input value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://www.partenaire.com" />
                      <p className="field-hint">Permet aux clients de consulter le catalogue constructeur.</p>
                   </div>
                   <div className="toggle-switch-group">
                      <span>Afficher sur le site public</span>
                      <label className="switch">
                         <input type="checkbox" checked={published} onChange={e => setPublished(e.target.checked)} />
                         <span className="slider round"></span>
                      </label>
                   </div>
                </div>
              )}

              {activeTab === 'media' && (
                <div className="tab-pane active animation-fade-in">
                   <div className="visual-studio">
                      <div className="preview-main-box supplier">
                         {previewUrl ? <img src={previewUrl} alt="Logo" /> : <div className="no-image"><Building size={48}/></div>}
                      </div>
                      <div className="visual-actions">
                         <label className="btn-upload-premium">
                            <Camera size={18}/> <span>Changer le logo</span>
                            <input type="file" hidden accept="image/*" onChange={handleLogoChange} />
                         </label>
                         {logo && <button type="button" className="btn-remove-media" onClick={() => setLogo(null)}><Trash2 size={16}/> Supprimer</button>}
                         <div className="visual-notes">
                            <Info size={14}/> <span>Format horizontal sur fond blanc ou transparent recommandé.</span>
                         </div>
                      </div>
                   </div>
                </div>
              )}
           </div>

           <footer className="modal-footer-premium">
              <button type="button" className="btn-modal-cancel" onClick={onClose}>Annuler</button>
              <button type="submit" className="btn-modal-save" disabled={saving}>
                 {saving ? <RefreshCcw size={18} className="spin"/> : <Save size={18}/>}
                 <span>{mode === 'create' ? 'Enregistrer' : 'Mettre à jour'}</span>
              </button>
           </footer>
        </form>
      </div>
    </div>
  );
}
